<template>
  <div v-if="show" class="ppv-modal-overlay" @click.self="handleClose">
    <div class="ppv-date-modal">
      <PpvModalHeader
        :title="$t('ppv.date_range_modal.title')"
        :subtitle="$t('ppv.date_range_modal.subtitle')"
        @close="handleClose"
      />
      <div class="ppv-date-modal-body">
        <PpvDateRangeInput
          :start-date="internalFrom"
          :end-date="internalTo"
          :min-start-date="minStartDate"
          :max-start-date="maxStartDate"
          :max-end-date="maxEndDate"
          :error="validationError"
          @update:startDate="internalFrom = $event"
          @update:endDate="internalTo = $event"
        />
        <div class="ppv-date-note">
          <span class="ppv-note-label">{{ $t('ppv.date_range_modal.note') }}</span>
          {{ $t('ppv.date_range_modal.note_text') }}
        </div>
      </div>
      <PpvModalFooter
        :confirm-label="$t('ppv.date_range_modal.btn_apply')"
        :is-valid="isValid"
        @cancel="handleClose"
        @confirm="handleApply"
      />
    </div>
  </div>
</template>

<script>
import { useDateRangeValidation } from './composables/useDateRangeValidation'
import { EVENTS, PVI_PANEL_NAME } from '@/plugins/analytics/events'
import { calcRangeDays } from '@/plugins/analytics/helpers'
import PpvModalHeader from './shared/PpvModalHeader.vue'
import PpvModalFooter from './shared/PpvModalFooter.vue'
import PpvDateRangeInput from './shared/PpvDateRangeInput.vue'

const { calcMaxStartDate, calcMinStartDate, calcMaxEndDate, validate } = useDateRangeValidation(90)

export default {
  name: 'PpvDateRangeModal',
  components: {
    PpvModalHeader,
    PpvModalFooter,
    PpvDateRangeInput,
  },
  props: {
    show: { type: Boolean, default: false },
    dateFrom: { type: String, default: '' },
    dateTo: { type: String, default: '' },
  },
  emits: ['close', 'apply'],
  data() {
    return {
      internalFrom: '',
      internalTo: '',
    }
  },
  computed: {
    parsedFrom() {
      return this.internalFrom ? new Date(this.internalFrom) : null
    },
    parsedTo() {
      return this.internalTo ? new Date(this.internalTo) : null
    },
    minStartDate() {
      return calcMinStartDate(this.internalTo)
    },
    maxStartDate() {
      return calcMaxStartDate()
    },
    maxEndDate() {
      return calcMaxEndDate(this.internalFrom)
    },
    validationError() {
      const errorKey = validate(this.parsedFrom, this.parsedTo)

      return errorKey ? this.$t(errorKey) : ''
    },
    isValid() {
      return Boolean(this.internalFrom && this.internalTo && !this.validationError)
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.internalFrom = this.dateFrom
          this.internalTo   = this.dateTo
        }
      },
    },
    internalFrom(newFrom) {
      if (!this.internalTo) return

      const newMax = this.maxEndDate

      if (this.internalTo > newMax || this.internalTo < newFrom) {
        this.internalTo = ''
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit('close')
    },
    handleApply() {
      if (!this.isValid) return

      const rangeDays = calcRangeDays(this.internalFrom, this.internalTo)
      this.$track(EVENTS.PV_DATE_RANGE_APPLIED, {
        date_from: this.internalFrom,
        date_to: this.internalTo,
        range_length_days: rangeDays,
        panel_name: PVI_PANEL_NAME,
      })

      this.$emit('apply', {
        from: this.internalFrom,
        to: this.internalTo,
      })
    },
  },
}
</script>

<style scoped>
.ppv-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
}
.ppv-date-modal {
  background: #fff;
  width: calc(100% - 32px);
  max-width: 440px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: modalFade 0.2s ease;
}
.ppv-date-modal-body {
  padding: 32px;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ppv-date-note {
  background: #f0f7ff;
  border: 1px solid #cce3ff;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 12px;
  color: #2c5282;
  line-height: 1.5;
}
.ppv-note-label {
  font-weight: 700;
}
@keyframes modalFade {
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0);    }
}
</style>
